import { BehaviorSubject } from 'rxjs';
import { AfterViewInit, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ArrHlp } from 'src/lib/fliko/helpers/array.helper';
import { FilterController } from '../../classes/filter.controller';
import { AsyncHlp } from '../../helpers/async.helper';
import { StringHelper } from '../../helpers/string.helper';
import { TemplateService } from '../../services/template-service/template.service';
import { DataTableColumn, DataTableConfig } from './models/data-table-config.models';
import { CrtRowClickedEvent } from './models/events.data';

@Component({
  selector: 'app-clickable-row-table[data][config]',
  templateUrl: './clickable-row-table.component.html',
  styleUrls: ['./clickable-row-table.component.scss']
})
export class ClickableRowTableComponent<T> implements AfterViewInit {
  @Input() set config(config: DataTableConfig) {
    this.applyConfig(config);
  }
  @Input() set data(data: T[]) {
    this.dataSource.data = data;
  }
  @Input() set search(value: string) {
    this.filterController?.setSearchQuery(value);
    this.applyFilter();
  }
  @Input() set filter(value: any) {
    this.filterController?.setFilters(value);
    this.applyFilter();
  }
  @Input() pageIndex = new BehaviorSubject<number>(0);
  @Input() headerRef!: TemplateRef<any>;
  @Input() templateRef!: TemplateRef<any>;
  @Input() columnRef!: TemplateRef<any>;
  @Input() loaded: boolean = true;

  /** @deprecated sends id of clicked row (if exists...) use "rowClicked" instead */
  @Output() rowClickedEvent = new EventEmitter<string>();

  @Output() rowClicked = new EventEmitter<CrtRowClickedEvent<T>>();

  // TODO: (by Andrzej) zaimplementować checkbox na poziomie komponentu

  public _config!: DataTableConfig;
  public dataSource: MatTableDataSource<any> = new MatTableDataSource();
  public dataColumns: DataTableColumn[] = [];
  public displayedColumns: string[] = [];

  public selectedRowId: string = '';

  private filterController?: FilterController<any, any>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(public templateService: TemplateService) {}

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (data, cVal) => {
      let returnData = data[cVal].value;
      if (this._config.dataColumns?.find((value) => value.value === cVal)?.valueIsNumber) {
        const asNumber = Number(returnData);
        returnData = Number.isNaN(asNumber) ? StringHelper.numString(returnData) : asNumber;
      }
      return returnData;
    };
    if (this._config.paginator) {
      this.dataSource.paginator = this.paginator;
      if (this.pageIndex) AsyncHlp.inNextTick(() => (this.paginator.pageIndex = this.pageIndex.value));
    }
  }

  public handleClickOnRow(row: T, rowIndex: number): void {
    if (this.dataSource.data !== undefined) {
      const rowId = (row as any).id.value;
      this.rowClickedEvent.emit(rowId);
      this.rowClicked.emit({ row, rowIndex, pageIndex: this.pageIndex.value });
      this.selectedRowId = rowId;
    }
  }

  public onPageChange(page: PageEvent): void {
    this.pageIndex.next(page.pageIndex);
  }

  private applyConfig(config: DataTableConfig): void {
    this._config = config;

    if (config.dataColumns) {
      this.dataColumns = config.dataColumns;
      this.displayedColumns = ArrHlp.filterMap(config.dataColumns, (c) => !c.hidden, (c) => c.value); // prettier-ignore
    }

    this.filterController = new FilterController<any, any>(config.searchKeyNames ?? [], config.filterPredicate ?? (() => true));

    this.dataSource.filterPredicate = (data): boolean => this.filterController?.predicate(data) ?? true;

    const defaultSorter = this.dataSource.sortData;

    this.dataSource.sortData = (data, matSort) => {
      if (!matSort.active && !matSort.direction) return data;
      const column = this.dataColumns.find((c) => c.value == matSort.active);

      return column?.customSorter ? column.customSorter(data, matSort) : defaultSorter(data, matSort);
    };
  }

  private applyFilter(): void {
    // HACK force mat table to reapply filter - our predicate ignores this value, using values from controllers instead
    this.dataSource.filter = Math.random().toString();
  }
}
